import { Route, Routes, useLocation } from "react-router-dom";
import "./App.css";

import Signin from "./pages/auth/Signin";
import Register from "./pages/auth/Register";
import ForgetPassword from "./pages/auth/ForgetPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Activate from "./pages/auth/Activate";

import Header from "./components/layouts/Header";
import Explore from "./pages/home/Explore";
import FollowedSellersAds from "./pages/home/FollowedSellersAds";
import ArtworkDetails from "./pages/home/Details";
import AboutUs from "./pages/support/AboutUs";
import HelpAndSupport from "./pages/support/HelpAndSupport";
import Sellers from "./pages/seller/Sellers";
import Details from "./pages/seller/Details";
import DashboardLayout from "./pages/dashboard/Layout";
import { useStore } from "./store";
import Loading from "./components/Loading";
import Home from "./pages/home/Home";
import BoneyardFixtures from "./pages/_boneyard/Fixtures";
import { Slide, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import TermsAndConditions from "./pages/support/TermsAndConditions";
import BiddingTerms from "./pages/support/BiddingTerms";
import SellingTerms from "./pages/support/SellingTerms";
import PrelaunchHeader from "./components/layouts/PrelaunchHeader";
import { useCallback, useEffect, useMemo } from "react";
import SearchResults from "./pages/home/SearchResults";
import PaymentCallback from "./pages/PaymentCallback";
import ProtectedRoute from "./components/layouts/ProtectedRoute";
import ProtectedAuthRoute from "./components/layouts/ProtectedAuthRoute";
import { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
// Side-effect import: the CLI-generated `./bones/registry` calls
// `registerBones(...)` and `configureBoneyard(...)` (from
// `boneyard.config.json`) at module load. Keep this import even before
// every <Skeleton> has been migrated — empty registries are no-ops.
import "./bones/registry";
import useGeolocation from "./hooks/useGeolocation";
import ThemeApplier from "./components/ThemeApplier";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

function App() {
  useGeolocation();
  const {
    isLoading,
    successMessage,
    errorMessage,
    infoMessage,
    setSuccessMessage,
    setInfoMessage,
    setErrorMessage,
    isFromPrelaunch,
    setIsFromPrelaunch,
  } = useStore((s: any) => s);

  const location = useLocation();

  useEffect(() => {
    const _isFromPrelaunch =
      location?.pathname === "/terms-and-conditions" &&
      location?.search === "?ref=prelaunch";
    setIsFromPrelaunch(_isFromPrelaunch);
  }, [location, setIsFromPrelaunch]);

  const noHeaderPaths = useMemo(
    () => [
      "/login",
      "/activate",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/dashboard",
    ],
    []
  );

  const pushError = useCallback(() => {
    if (errorMessage?.length > 0) {
      toast.error(errorMessage, {
        containerId: "toasts-error",
        onClose: () => setErrorMessage(""),
      });
    }
  }, [errorMessage, setErrorMessage]);

  const pushSuccess = useCallback(() => {
    if (successMessage?.length > 0) {
      toast.success(successMessage, {
        containerId: "toasts-success-info",
        onClose: () => setSuccessMessage(""),
      });
    }
  }, [successMessage, setSuccessMessage]);

  const pushInfo = useCallback(() => {
    if (infoMessage?.length > 0) {
      toast.info(infoMessage, {
        containerId: "toasts-success-info",
        onClose: () => setInfoMessage(""),
      });
    }
  }, [infoMessage, setInfoMessage]);

  useEffect(() => {
    pushError();
    pushSuccess();
    pushInfo();
  }, [
    pushError,
    pushSuccess,
    pushInfo,
    errorMessage,
    successMessage,
    infoMessage,
  ]);

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <SkeletonTheme
      baseColor="var(--skeleton-base)"
      highlightColor="var(--skeleton-highlight)"
      duration={1.4}
    >
    <ThemeApplier />
    <div className="App">
      <ToastContainer
        containerId="toasts-success-info"
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      <ToastContainer
        containerId="toasts-error"
        position="top-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      {isLoading && <Loading />}

      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}

      {!noHeaderPaths.some((k: string) => location.pathname?.match(k)) &&
        !isFromPrelaunch && <Header />}

      {isFromPrelaunch ? (
        <>
          <PrelaunchHeader />
          <Routes>
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
          </Routes>
        </>
      ) : (
        <Routes>
          <Route
            path="/login"
            element={
              <ProtectedAuthRoute>
                <Signin />
              </ProtectedAuthRoute>
            }
          />
          <Route
            path="/register"
            element={
              <ProtectedAuthRoute>
                <Register />
              </ProtectedAuthRoute>
            }
          />
          <Route
            path="/activate"
            element={
              <ProtectedAuthRoute>
                <Activate />
              </ProtectedAuthRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <ProtectedAuthRoute>
                <ForgetPassword />
              </ProtectedAuthRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <ProtectedAuthRoute>
                <ResetPassword />
              </ProtectedAuthRoute>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/bidding-terms" element={<BiddingTerms />} />
          <Route path="/selling-terms" element={<SellingTerms />} />
          <Route path="/" element={<Home />} />
          <Route path="/_boneyard" element={<BoneyardFixtures />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/explore/:id" element={<Explore />} />
          <Route
            path="/followed-sellers"
            element={
              <ProtectedRoute>
                <FollowedSellersAds />
              </ProtectedRoute>
            }
          />
          <Route path="/details/:id" element={<ArtworkDetails />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/help-and-support" element={<HelpAndSupport />} />
          <Route path="/sellers" element={<Sellers />} />
          <Route path="/seller/:id" element={<Details />} />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-callback"
            element={
              <ProtectedRoute>
                <PaymentCallback />
              </ProtectedRoute>
            }
          />
        </Routes>
      )}
    </div>
    </SkeletonTheme>
  );
}

export default App;
